'use client';

import Link from 'next/link';
import type { MouseEvent, ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface FilterPermanentLinkProps {
  href: string;
  children: ReactNode;
  className?: string;
  title?: string;
  isSeoOnly?: boolean;
  onNavigate?: (event: { preventDefault: () => void }) => void;
}

const INTERACTIVE_LINK_CLASSNAME =
  'min-w-0 truncate hover:text-[#1d4ed8] hover:underline focus-visible:text-[#1d4ed8] focus-visible:underline';

const SEO_ONLY_LINK_CLASSNAME =
  'min-w-0 truncate text-inherit no-underline hover:text-inherit hover:no-underline';

const stopClickPropagation = (event: MouseEvent<HTMLAnchorElement>): void => {
  event.stopPropagation();
};

const isModifiedClick = (event: MouseEvent<HTMLAnchorElement>): boolean =>
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

const preventSeoOnlyNavigation = (event: MouseEvent<HTMLAnchorElement>): void => {
  if (isModifiedClick(event)) {
    event.stopPropagation();
    return;
  }

  event.preventDefault();
};

export const FilterPermanentLink = ({
  href,
  children,
  className,
  title,
  isSeoOnly = false,
  onNavigate,
}: FilterPermanentLinkProps) => {
  if (isSeoOnly) {
    return (
      <a
        href={href}
        title={title}
        tabIndex={-1}
        data-filter-permalink="true"
        onClick={preventSeoOnlyNavigation}
        className={cn(SEO_ONLY_LINK_CLASSNAME, className)}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      href={href}
      prefetch={false}
      title={title}
      data-filter-permalink="true"
      onClick={stopClickPropagation}
      onNavigate={onNavigate}
      className={cn(INTERACTIVE_LINK_CLASSNAME, className)}
    >
      {children}
    </Link>
  );
};
